import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayMode, BodyCompositionStatus } from './types';

interface SegmentalMetricCardProps {
    label: string;
    valueKg?: number;
    valuePercentage?: number;
    displayMode?: DisplayMode;
}

const statusTranslationKeys: Record<BodyCompositionStatus, string> = {
    [BodyCompositionStatus.Low]: 'inbodyComposition.viewModal.statusLow',
    [BodyCompositionStatus.Average]: 'inbodyComposition.viewModal.statusAverage',
    [BodyCompositionStatus.High]: 'inbodyComposition.viewModal.statusHigh',
};

/**
 * Calculate status based on percentage value
 * - high: >= 110%
 * - average: 90% - 109%
 * - low: otherwise
 */
const getStatusFromPercentage = (percentage: number): BodyCompositionStatus => {
    if (percentage >= 110) {
        return BodyCompositionStatus.High;
    }
    if (percentage >= 90 && percentage <= 109) {
        return BodyCompositionStatus.Average;
    }
    return BodyCompositionStatus.Low;
};

const SegmentalMetricCard = ({
    label,
    valueKg,
    valuePercentage,
    displayMode = DisplayMode.Both,
}: SegmentalMetricCardProps) => {
    const { t } = useTranslation('physicalResults');
    const notAvailable = t('inbodyComposition.viewModal.notAvailable');

    const status = useMemo(
        () => (valuePercentage ? getStatusFromPercentage(valuePercentage) : undefined),
        [valuePercentage]
    );

    const formattedKg = valueKg ? t('inbodyComposition.viewModal.kgSuffix', { value: valueKg.toFixed(2) }) : undefined;

    const formattedPercentage = valuePercentage
        ? t('inbodyComposition.viewModal.percentageSuffix', { value: valuePercentage.toFixed(2) })
        : undefined;

    // Display both kg and percentage
    if (displayMode === DisplayMode.Both) {
        return (
            <div className="inline-flex flex-col items-center justify-start gap-0.5 self-stretch rounded-[10px] border-2 border-white bg-neutral-500/5 p-5 backdrop-blur-[7.5px]">
                <div className="text-foreground-static/60 text-center text-xs leading-4 font-medium">{label}</div>
                <div className="inline-flex items-start justify-start gap-2.5">
                    <div className="text-foreground-static text-xl leading-7 font-bold">
                        {formattedKg || notAvailable}
                    </div>
                    <div className="bg-foreground-static/20 w-0.5 self-stretch" />
                    <div className="text-foreground-static text-xl leading-7 font-bold">
                        {formattedPercentage || notAvailable}
                    </div>
                </div>
                {status && (
                    <div className="text-center text-xs leading-4 font-bold text-teal-600">
                        {t(statusTranslationKeys[status])}
                    </div>
                )}
            </div>
        );
    }

    // Single value display (kg or percentage)
    const value = displayMode === DisplayMode.Kg ? formattedKg : formattedPercentage;
    return (
        <div className="flex w-full flex-col items-center gap-0.5 rounded-[10px] border-2 border-white bg-neutral-500/5 p-5 backdrop-blur-[7.5px]">
            <div className="text-foreground-static text-lg leading-7 font-bold">{value || notAvailable}</div>
            <div className="text-foreground-static/60 text-center text-base leading-6 font-medium">{label}</div>
            {status && (
                <div className="text-center text-base leading-6 font-bold text-teal-600">
                    {t(statusTranslationKeys[status])}
                </div>
            )}
        </div>
    );
};

export default SegmentalMetricCard;
